<template>
  <section>
    <div class="columns">
      <div class="column">
        <div class="container mt-3">
          <!-- Loading -->
          <div v-if="loading" class="has-text-centered py-6">
            <b-loading :active="loading" :is-full-page="false"></b-loading>
            <p class="mt-4">Cargando información del desempeño...</p>
          </div>

          <template v-else-if="performance">
            <!-- Hero Header -->
            <section class="hero is-primary welcome is-small mb-4">
              <div class="hero-body">
                <div class="container">
                  <h1 class="title">
                    <i class="fas fa-clipboard-check"></i>
                    Precalificación de Desempeño
                    <span v-if="isCommitteeMember" class="tag is-info is-light ml-3">
                      <span class="icon"><i class="fas fa-eye"></i></span>
                      <span>Solo Lectura</span>
                    </span>
                  </h1>
                  <a href="/performance_precalifications" class="button is-light is-small">
                    <span class="icon">
                      <i class="fas fa-arrow-left"></i>
                    </span>
                    <span>Volver a la Bandeja</span>
                  </a>
                </div>
              </div>
            </section>
            
            <!-- Banner informativo para comisión -->
            <div v-if="isCommitteeMember" class="notification is-info is-light mb-4">
              <div class="content">
                <p class="has-text-weight-bold mb-2">
                  <span class="icon"><i class="fas fa-info-circle"></i></span>
                  Comisión Precalificadora - Miembro {{ committeeMemberNumber }}
                </p>
                <p class="mb-0">
                  Está visualizando este desempeño en <strong>modo solo lectura</strong>. 
                  Puede revisar las calificaciones asignadas por el director y las evidencias presentadas por el académico.
                  Una vez que haya revisado toda la información, puede proceder a <strong>firmar esta precalificación</strong> para validarla.
                </p>
              </div>
            </div>

            <!-- Información del Académico -->
            <div class="box">
              <h4 class="title is-4">
                <span class="icon-text">
                  <span class="icon has-text-info">
                    <i class="fas fa-user"></i>
                  </span>
                  <span>Información del Académico</span>
                </span>
              </h4>
              <div class="columns">
                <div class="column is-6">
                  <p><strong>Nombre:</strong> {{ academicName }}</p>
                  <p><strong>Unidad:</strong> {{ unitName }}</p>
                </div>
                <div class="column is-6">
                  <p><strong>Año:</strong> {{ performance.year }}</p>
                  <p><strong>Total Actividades:</strong> {{ activityPerformances.length }}</p>
                </div>
              </div>
            </div>

            <!-- Ponderaciones por Área -->
            <div v-if="isDirector || isCommitteeMember" class="box has-background-info-light">
              <div class="level">
                <div class="level-left">
                  <div class="level-item">
                    <h4 class="title is-4">
                      <span class="icon-text">
                        <span class="icon has-text-info">
                          <i class="fas fa-balance-scale"></i>
                        </span>
                        <span>Ponderaciones por Área</span>
                      </span>
                    </h4>
                  </div>
                </div>
                <div v-if="isDirector" class="level-right">
                  <div class="level-item">
                    <b-button
                      type="is-info"
                      icon-left="edit"
                      size="is-small"
                      @click="openAreaWeightsModal"
                    >
                      {{ areaWeights.length > 0 ? 'Editar Ponderaciones' : 'Asignar Ponderaciones' }}
                    </b-button>
                  </div>
                </div>
              </div>

              <div v-if="areaWeights.length > 0" class="content">
                <div class="columns is-multiline">
                  <div v-for="weight in areaWeights" :key="weight.id" class="column is-6">
                    <b-message type="is-info" size="is-small" :closable="false">
                      <strong>{{ weight.area_name }}:</strong> {{ weight.weight_percentage }}%
                    </b-message>
                  </div>
                </div>

                <div v-if="weightedScoreBreakdown" class="mt-4">
                  <h5 class="subtitle is-5">Cálculo de Nota Final Ponderada:</h5>
                  <div class="content">
                    <table class="table is-striped is-narrow is-fullwidth">
                      <thead>
                        <tr>
                          <th>Área</th>
                          <th>Promedio Área</th>
                          <th>Ponderación</th>
                          <th>Aporte</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="item in weightedScoreBreakdown.breakdown" :key="item.area_name">
                          <td>{{ item.area_name }}</td>
                          <td>{{ parseFloat(item.area_average).toFixed(2) }}</td>
                          <td>{{ item.weight_percentage }}%</td>
                          <td><strong>{{ parseFloat(item.weighted_contribution).toFixed(2) }}</strong></td>
                        </tr>
                      </tbody>
                      <tfoot>
                        <tr class="has-background-success-light">
                          <td colspan="3"><strong>NOTA FINAL PONDERADA:</strong></td>
                          <td><strong class="is-size-5">{{ parseFloat(weightedScoreBreakdown.total_score).toFixed(2) }}</strong></td>
                        </tr>
                        <tr v-if="finalScoreCategory" :class="`has-background-${finalScoreCategory.type.replace('is-', '')}-light`">
                          <td colspan="3"><strong>CATEGORÍA:</strong></td>
                          <td>
                            <b-tag :type="finalScoreCategory.type" size="is-medium">
                              <strong>{{ finalScoreCategory.label }}</strong>
                            </b-tag>
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              </div>
              <div v-else>
                <b-message type="is-warning" has-icon>
                  Aún no se han asignado ponderaciones para las áreas académicas. Haz clic en "Asignar Ponderaciones" para configurarlas.
                </b-message>
              </div>
            </div>

            <!-- Actividades para Calificar -->
            <h4 class="title is-4 mt-5">
              Actividades Declaradas ({{ displayedActivities.length }})
            </h4>

            <!-- Pestañas de filtro por Área Académica -->
            <div class="tabs is-toggle is-fullwidth mb-4">
              <ul>
                <li :class="{ 'is-active': selectedAreaName === 'Todas' }">
                  <a @click.prevent="selectArea('Todas')">Todas</a>
                </li>
                <li v-for="tab in areaTabs" :key="tab" :class="{ 'is-active': selectedAreaName === tab }">
                  <a
                    href="#"
                    @click.prevent="selectArea(tab)"
                    :class="{ 'has-text-grey-light': !hasActivitiesForTab(tab) }"
                    :aria-disabled="!hasActivitiesForTab(tab)"
                    :title="hasActivitiesForTab(tab) ? tab : tab + ' (sin actividades)'"
                  >
                    {{ tab }}
                    <span v-if="getAreaCount(tab) > 0" class="tag is-light ml-2">{{ getAreaCount(tab) }}</span>
                  </a>
                </li>
              </ul>
            </div>

            <div 
              v-for="(activity, index) in displayedActivities" 
              :key="activity.id"
              class="box mb-4"
              :class="getActivityBoxClass(activity)"
            >
              <!-- Card Header -->
              <div class="level mb-3">
                <div class="level-left">
                  <div class="level-item">
                    <h5 class="title is-5 mb-0">
                      <span class="icon-text">
                        <span class="icon">
                          <i class="fas fa-tasks"></i>
                        </span>
                        <span>{{ index + 1 }}. {{ activity.academic_activity ? activity.academic_activity.name : 'Actividad sin nombre' }}</span>
                      </span>
                    </h5>
                  </div>
                </div>
                <div class="level-right">
                  <div class="level-item">
                    <b-tag :type="getStatusTagType(activity)" size="is-medium">
                      {{ getStatusText(activity) }}
                    </b-tag>
                  </div>
                </div>
              </div>

              <!-- Card Body -->
              <div class="columns">
                <div class="column is-4">
                  <p>
                    <strong>Horas Semestre 1:</strong> {{ activity.hours_completed_semester_1 || 0 }}
                    <b-tag v-if="activity.sai_hours_source" type="is-info" size="is-small" class="ml-2">
                      <b-icon icon="check-circle" size="is-small"></b-icon>
                      <span class="ml-1">SAI</span>
                    </b-tag>
                  </p>
                  <p>
                    <strong>Horas Semestre 2:</strong> {{ activity.hours_completed_semester_2 || 0 }}
                    <b-tag v-if="activity.sai_hours_source" type="is-info" size="is-small" class="ml-2">
                      <b-icon icon="check-circle" size="is-small"></b-icon>
                      <span class="ml-1">SAI</span>
                    </b-tag>
                  </p>
                  <p><strong>Total Horas:</strong> {{ getTotalHours(activity) }}</p>
                  <p v-if="activity.sai_hours_source" class="has-text-info is-size-7 mt-2">
                    <b-icon icon="check-circle" size="is-small"></b-icon>
                    Datos validados por SAI (Código: {{ activity.subject_code }})
                  </p>
                </div>
                <div class="column is-8">
                  <p><strong>Descripción:</strong> {{ activity.description || 'Sin descripción' }}</p>
                </div>
              </div>

              <!-- Evidencias -->
              <div v-if="activity.evidences && activity.evidences.length > 0" class="mt-3">
                <h6 class="subtitle is-6">
                  <span class="icon-text">
                    <span class="icon has-text-info">
                      <i class="fas fa-paperclip"></i>
                    </span>
                    <span>Evidencias ({{ activity.evidences.length }})</span>
                  </span>
                </h6>
                <div class="content">
                  <ul>
                    <li v-for="evidence in activity.evidences" :key="evidence.id">
                      <a :href="getEvidenceUrl(evidence)" target="_blank" class="has-text-info is-underlined">
                        <span class="icon is-small">
                          <i class="fas fa-download"></i>
                        </span>
                        <span>{{ evidence.file_name || 'Evidencia' }}</span>
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
              <div v-else>
                <!-- Mensaje informativo si las horas vienen de SAI -->
                <b-message v-if="activity.sai_hours_source" type="is-info" size="is-small">
                  <strong>Evidencia automática:</strong> Esta actividad no requiere evidencias manuales porque las horas fueron obtenidas y validadas automáticamente desde el Sistema Académico Integrado (SAI).
                </b-message>
                <!-- Mostrar mensaje solo si la actividad requiere evidencias obligatorias -->
                <b-message v-else-if="activityRequiresEvidence(activity)" type="is-danger" size="is-small">
                  <strong>Sin evidencias:</strong> Esta actividad excede las horas comprometidas y no puede ser calificada hasta que se adjunten evidencias.
                </b-message>
                <b-message v-else type="is-info" size="is-small">
                  <strong>Sin evidencias:</strong> Esta actividad no tiene evidencias adjuntas (opcional).
                </b-message>
              </div>

              <!-- Calificación Actual -->
              <div v-if="activity.precalification_score !== null" class="mt-4">
                <b-message type="is-info" has-icon>
                  <div class="columns is-centered">
                    <div class="column is-3 has-text-centered">
                      <p class="mb-1"><strong>Nota:</strong></p>
                      <p class="mb-0 is-size-5"><strong>{{ activity.precalification_score }}</strong></p>
                    </div>
                    <div class="column is-3 has-text-centered">
                      <p class="mb-1"><strong>Categoría:</strong></p>
                      <p class="mb-0 is-size-5"><strong>{{ activity.precalification_label }}</strong></p>
                    </div>
                    <div class="column is-3 has-text-centered">
                      <p class="mb-1"><strong>Calificada:</strong></p>
                      <p class="mb-0 is-size-5"><strong>{{ formatDate(activity.precalified_at) }}</strong></p>
                    </div>
                  </div>
                  <div v-if="activity.precalification_comment" class="mt-4 pt-3" style="border-top: 1px solid rgba(0,0,0,0.1);">
                    <p class="mb-1"><strong>Observaciones:</strong></p>
                    <p class="mb-0 pl-4">{{ activity.precalification_comment }}</p>
                  </div>
                </b-message>
              </div>

              <!-- Botón Calificar (solo para director) -->
              <div v-if="canEditScores" class="mt-3">
                <b-tooltip 
                  v-if="!canScoreActivities && isDirector"
                  label="Debe asignar ponderaciones antes de calificar actividades"
                  type="is-warning"
                  position="is-top"
                  multilined
                >
                  <b-button 
                    @click="openScoreModal(activity)"
                    type="is-primary"
                    icon-left="star"
                    :disabled="!canScoreActivities || (activityRequiresEvidence(activity) && (!activity.evidences || activity.evidences.length === 0))"
                  >
                    {{ activity.precalification_score !== null ? 'Editar Calificación' : 'Calificar Actividad' }}
                  </b-button>
                </b-tooltip>
                <b-button 
                  v-else
                  @click="openScoreModal(activity)"
                  type="is-primary"
                  icon-left="star"
                  :disabled="!canScoreActivities || (activityRequiresEvidence(activity) && (!activity.evidences || activity.evidences.length === 0))"
                >
                  {{ activity.precalification_score !== null ? 'Editar Calificación' : 'Calificar Actividad' }}
                </b-button>
                <p v-if="!canScoreActivities && isDirector" class="help has-text-danger mt-2">
                  <span class="icon"><i class="fas fa-exclamation-triangle"></i></span>
                  Debe asignar ponderaciones antes de calificar actividades
                </p>
              </div>
              
              <!-- Mensaje de solo lectura para comisión -->
              <div v-else-if="isCommitteeMember" class="mt-3">
                <b-message type="is-info" size="is-small" has-icon>
                  <strong>Modo solo lectura:</strong> Como miembro de la comisión precalificadora, puede revisar las calificaciones asignadas por el director pero no puede editarlas. Su rol es validar esta información mediante su firma.
                </b-message>
              </div>
            </div>

            <!-- Sección de Firmas y Acciones Globales -->
            <div class="columns mt-5">
              <div class="column is-6">
                <div class="box">
                  <h5 class="title is-5">
                    <span class="icon-text">
                      <span class="icon has-text-grey">
                        <i class="fas fa-signature"></i>
                      </span>
                      <span>Firmas de la Comisión</span>
                    </span>
                  </h5>
                  <div class="content">
                    <div class="mb-3">
                      <span class="icon" :class="signatures.director.signed ? 'has-text-success' : 'has-text-warning'">
                        <i :class="signatures.director.signed ? 'fas fa-check-circle' : 'fas fa-clock'"></i>
                      </span>
                      <strong>Director/a de Departamento:</strong>
                      <span class="ml-2">{{ signatures.director.name }}</span>
                      <b-tag class="ml-2" :type="signatures.director.signed ? 'is-success' : 'is-warning'">
                        {{ signatures.director.signed ? '✓ Firmado' : '⏳ Pendiente' }}
                      </b-tag>
                    </div>
                    <div class="mb-3">
                      <span class="icon" :class="signatures.par_interno.signed ? 'has-text-success' : 'has-text-warning'">
                        <i :class="signatures.par_interno.signed ? 'fas fa-check-circle' : 'fas fa-clock'"></i>
                      </span>
                      <strong>Comisión Miembro 1:</strong>
                      <span class="ml-2">{{ signatures.par_interno.name }}</span>
                      <b-tag class="ml-2" :type="signatures.par_interno.signed ? 'is-success' : 'is-warning'">
                        {{ signatures.par_interno.signed ? '✓ Firmado' : '⏳ Pendiente' }}
                      </b-tag>
                    </div>
                    <div>
                      <span class="icon" :class="signatures.par_externo.signed ? 'has-text-success' : 'has-text-warning'">
                        <i :class="signatures.par_externo.signed ? 'fas fa-check-circle' : 'fas fa-clock'"></i>
                      </span>
                      <strong>Comisión Miembro 2:</strong>
                      <span class="ml-2">{{ signatures.par_externo.name }}</span>
                      <b-tag class="ml-2" :type="signatures.par_externo.signed ? 'is-success' : 'is-warning'">
                        {{ signatures.par_externo.signed ? '✓ Firmado' : '⏳ Pendiente' }}
                      </b-tag>
                    </div>
                  </div>
                </div>
              </div>

              <div class="column is-6">
                <div class="box">
                  <h5 class="title is-5">
                    <span class="icon-text">
                      <span class="icon has-text-info">
                        <i class="fas fa-cogs"></i>
                      </span>
                      <span>Acciones</span>
                    </span>
                  </h5>
                  
                  <b-button
                    class="mb-2"
                    @click="previewReportPDF"
                    type="is-info"
                    icon-left="file-pdf"
                    expanded
                    :loading="generatingPdf"
                  >
                    Ver informe declaración
                  </b-button>
                  
                  <b-tooltip 
                    v-if="!canScoreActivities && isDirector"
                    label="Debe asignar ponderaciones antes de firmar la precalificación"
                    type="is-warning"
                    position="is-top"
                    multilined
                    expanded
                  >
                    <b-button 
                      @click="signPrecalification"
                      type="is-success"
                      icon-left="signature"
                      expanded
                      :disabled="currentUserSigned || !canSign || signingPrecalification"
                      :loading="signingPrecalification"
                    >
                      {{ currentUserSigned ? '✓ Ya Firmado' : 'Firmar Precalificación' }}
                    </b-button>
                  </b-tooltip>
                  <b-tooltip 
                    v-else-if="!canSign && !currentUserSigned"
                    :label="isSecretariaAcademica ? 'El rol de Secretaría Académica no puede firmar precalificaciones' : 'Debe calificar todas las actividades con evidencias antes de firmar'"
                    :type="isSecretariaAcademica ? 'is-warning' : 'is-info'"
                    position="is-top"
                    multilined
                    expanded
                  >
                    <b-button 
                      @click="signPrecalification"
                      type="is-success"
                      icon-left="signature"
                      expanded
                      :disabled="currentUserSigned || !canSign || signingPrecalification || isSecretariaAcademica"
                      :loading="signingPrecalification"
                    >
                      {{ currentUserSigned ? '✓ Ya Firmado' : 'Firmar Precalificación' }}
                    </b-button>
                  </b-tooltip>
                  <b-button 
                    v-else
                    @click="signPrecalification"
                    type="is-success"
                    icon-left="signature"
                    expanded
                    :disabled="currentUserSigned || !canSign || signingPrecalification || isSecretariaAcademica"
                    :loading="signingPrecalification"
                  >
                    {{ currentUserSigned ? '✓ Ya Firmado' : 'Firmar Precalificación' }}
                  </b-button>

                  <p v-if="currentUserSigned" class="help has-text-success mt-2">
                    <span class="icon"><i class="fas fa-check-circle"></i></span>
                    Ya has firmado esta precalificación
                  </p>
                  <p v-else-if="isSecretariaAcademica" class="help has-text-warning mt-2">
                    <span class="icon"><i class="fas fa-info-circle"></i></span>
                    El rol de Secretaría Académica no puede firmar precalificaciones
                  </p>
                  <p v-else-if="!canScoreActivities && isDirector" class="help has-text-danger mt-2">
                    <span class="icon"><i class="fas fa-exclamation-triangle"></i></span>
                    Debe asignar ponderaciones antes de firmar
                  </p>
                  <p v-else-if="!canSign" class="help has-text-grey mt-2">
                    Debes calificar todas las actividades con evidencias antes de firmar
                  </p>
                </div>
              </div>
            </div>

            <!-- Modales -->
            <b-modal v-model="showScoreModal" has-modal-card trap-focus :destroy-on-hide="false" aria-role="dialog" aria-modal>
              <template #default="props">
                <ActivityScoreModal
                  :activity="selectedActivity"
                  :performance-id="performanceId"
                  @close="props.close"
                  @scored="onActivityScored"
                />
              </template>
            </b-modal>

            <b-modal v-model="showAreaWeightsModal" has-modal-card trap-focus :destroy-on-hide="false" aria-role="dialog" aria-modal>
              <template #default="props">
                <AreaWeightsModal
                  :performance-id="performanceId"
                  :academic-name="academicName"
                  :is-edit="areaWeights.length > 0"
                  @close="props.close"
                  @saved="onAreaWeightsSaved"
                />
              </template>
            </b-modal>

            <!-- Modal de Vista Previa del PDF -->
            <b-modal v-model="showPdfPreviewModal" :width="1200" scroll="keep" :can-cancel="['escape', 'outside']">
              <div class="box" style="height: 90vh;">
                <div class="level mb-3">
                  <div class="level-left">
                    <div class="level-item">
                      <h4 class="title is-4">
                        <span class="icon-text">
                          <span class="icon has-text-info">
                            <i class="fas fa-file-pdf"></i>
                          </span>
                          <span>Vista Previa - Informe de Declaración</span>
                        </span>
                      </h4>
                    </div>
                  </div>
                  <div class="level-right">
                    <div class="level-item">
                      <b-button
                        @click="downloadCurrentPdf"
                        type="is-success"
                        icon-left="download"
                      >
                        Descargar PDF
                      </b-button>
                    </div>
                    <div class="level-item">
                      <b-button
                        @click="showPdfPreviewModal = false"
                        type="is-light"
                        icon-left="times"
                      >
                        Cerrar
                      </b-button>
                    </div>
                  </div>
                </div>
                <iframe
                  v-if="pdfPreviewUrl"
                  :src="pdfPreviewUrl"
                  style="width: 100%; height: calc(100% - 60px); border: 1px solid #ddd; border-radius: 4px;"
                ></iframe>
              </div>
            </b-modal>

            <!-- Modal de Verificación de Firma -->
            <b-modal 
              v-model="showVerificationModal" 
              has-modal-card 
              trap-focus 
              :destroy-on-hide="false"
              :can-cancel="['escape']"
              aria-role="dialog" 
              aria-modal
            >
              <div class="modal-card verification-modal-card">
                <header class="modal-card-head">
                  <p class="modal-card-title">
                    <span class="icon"><i class="fas fa-shield-alt"></i></span>
                    Verificación de Firma
                  </p>
                </header>
                <section class="modal-card-body">
                  <div class="content has-text-centered mb-4">
                    <p class="mb-2">
                      Se ha enviado un código de verificación a:
                    </p>
                    <p class="has-text-weight-bold has-text-primary">
                      {{ verificationEmail }}
                    </p>
                  </div>
                  
                  <!-- Mensaje de error inline -->
                  <b-message 
                    v-if="verificationError" 
                    type="is-danger" 
                    has-icon 
                    class="mb-4"
                  >
                    {{ verificationError }}
                  </b-message>
                  
                  <!-- Mensaje de éxito al reenviar -->
                  <b-message 
                    v-if="verificationSuccess" 
                    type="is-success" 
                    has-icon 
                    class="mb-4"
                  >
                    {{ verificationSuccess }}
                  </b-message>
                  
                  <b-field 
                    label="Ingrese el código de 6 caracteres:"
                    :type="verificationError ? 'is-danger' : ''"
                  >
                    <b-input
                      v-model="verificationCode"
                      placeholder="XXXXXX"
                      maxlength="6"
                      size="is-medium"
                      class="verification-input"
                      @keyup.enter="confirmVerification"
                      ref="verificationInput"
                    ></b-input>
                  </b-field>
                </section>
                <footer class="modal-card-foot verification-footer">
                  <b-button 
                    type="is-primary" 
                    icon-left="check"
                    :loading="verifyingCode"
                    :disabled="!verificationCode || verificationCode.length !== 6 || verifyingCode"
                    @click="confirmVerification"
                  >
                    Verificar y Firmar
                  </b-button>
                  <b-button 
                    type="is-info is-light" 
                    icon-left="redo"
                    :loading="resendingCode"
                    :disabled="resendCooldownSeconds > 0 || resendingCode"
                    @click="resendVerificationCode"
                  >
                    {{ resendCooldownSeconds > 0 ? `Reenviar (${resendCooldownSeconds}s)` : 'Reenviar Código' }}
                  </b-button>
                  <b-button 
                    @click="closeVerificationModal"
                  >
                    Cancelar
                  </b-button>
                </footer>
              </div>
            </b-modal>
          </template>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import ActivityScoreModal from './ActivityScoreModal.vue'
import AreaWeightsModal from './AreaWeightsModal.vue'
import jsPDF from "jspdf";
import "jspdf-autotable";

export default {
  name: 'PerformanceDetailPrecalification',
  
  components: {
    ActivityScoreModal,
    AreaWeightsModal
  },
  
  props: {
    performanceId: {
      type: Number,
      required: true
    },
    userRole: {
      type: String,
      required: false,
      default: null
    }
  },
  
  data() {
    return {
      performance: null,
      activityPerformances: [],
      signatures: {
        director: { name: '', signed: false },
        par_interno: { name: '', signed: false },
        par_externo: { name: '', signed: false }
      },
      currentUserRole: null,
      currentUserSigned: false,
      canEditScoresFromBackend: true, // Se actualizará con respuesta del backend
      committeeMemberNumber: null, // 1 o 2, para saber qué miembro del comité es
      showScoreModal: false,
      showAreaWeightsModal: false,
      showPdfPreviewModal: false,
      showVerificationModal: false, // Modal de verificación de firma
      pdfPreviewUrl: null,
      currentPdfDoc: null,
      currentPdfFilename: '',
      selectedActivity: null,
      loading: false,
      generatingPdf: false,
      signingPrecalification: false, // Indica si está en proceso de firma
      resendingCode: false, // Indica si está reenviando el código
      verifyingCode: false, // Indica si está verificando el código
      verificationEmail: '', // Email para reenviar código
      verificationCode: '', // Código ingresado por el usuario
      verificationError: '', // Mensaje de error en verificación
      verificationSuccess: '', // Mensaje de éxito en verificación
      resendCooldownSeconds: 0, // Tiempo de espera para reenviar (en segundos)
      resendCooldownInterval: null, // Intervalo para contar hacia atrás
      resendCooldownMax: 60, // Tiempo máximo de bloqueo en segundos (1 minuto)
      areaWeights: [],
      weightedScoreBreakdown: null,
      academicInfo: null, // Información académica del performance
      unitNameFromBackend: null, // Nombre de la unidad del académico
      // Tabs: listado de tipos de áreas (usar mismo orden que en otras vistas)
      areaTabs: [
        'Docencia',
        'Investigación y Desarrollo',
        'Asistencia Técnica',
        'Perfeccionamiento',
        'Administración Académica',
        'Extensión-VIME',
        'Educación Continua-VIME'
      ],
      availableAreas: [],
      areaCounts: {},
      selectedAreaName: 'Todas'
    }
  },
  
  computed: {
    academicName() {
      if (!this.performance?.person) return 'N/A'
      const person = this.performance.person
      const secondSurname = person.second_surname ? ` ${person.second_surname}` : ''
      return `${person.name} ${person.last_name}${secondSurname}`
    },
    
    unitName() {
      // Usar el nombre de la unidad que viene del backend
      return this.unitNameFromBackend || 'N/A'
    },

    isDirector() {
      return this.currentUserRole === 'director'
    },

    isCommitteeMember() {
      return this.currentUserRole === 'comision_precalificador'
    },

    isSecretariaAcademica() {
      return this.userRole === 'SECRETARIA ACADEMICA FING'
    },

    canEditScores() {
      // Solo el director puede editar/calificar actividades
      // Los miembros del comité solo pueden ver (modo solo lectura)
      return this.isDirector
    },

    canScoreActivities() {
      // Solo el director puede calificar, y necesita tener ponderaciones asignadas
      if (!this.canEditScores) return false
      return this.areaWeights && this.areaWeights.length > 0
    },
    
    canSign() {
      // SECRETARIA ACADEMICA no puede firmar
      if (this.isSecretariaAcademica) return false
      
      // No puede firmar si ya firmó
      if (this.currentUserSigned) return false

      // Director: necesita ponderaciones asignadas
      if (this.isDirector && !this.canScoreActivities) return false
      
      // Puede firmar si TODAS las actividades están calificadas
      // (independientemente de si tienen evidencias o no)
      return this.activityPerformances.length > 0 &&
             this.activityPerformances.every(a => a.precalification_score !== null)
    },

    // Calcula la categoría de la nota final ponderada
    finalScoreCategory() {
      if (!this.weightedScoreBreakdown || !this.weightedScoreBreakdown.total_score) {
        return null
      }
      
      const score = parseFloat(this.weightedScoreBreakdown.total_score)
      
      if (score >= 3.70 && score <= 4.00) {
        return { label: 'Sobresaliente', type: 'is-success' }
      } else if (score >= 2.80 && score < 3.70) {
        return { label: 'Bueno', type: 'is-info' }
      } else if (score >= 2.00 && score < 2.80) {
        return { label: 'Aceptable', type: 'is-warning' }
      } else if (score >= 1.50 && score < 2.00) {
        return { label: 'Condicional', type: 'is-warning' }
      } else if (score >= 0.00 && score < 1.50) {
        return { label: 'Insuficiente', type: 'is-danger' }
      }
      
      return null
    },

    // Actividades filtradas según la pestaña seleccionada
    displayedActivities() {
      if (!this.activityPerformances) return []
      if (this.selectedAreaName === 'Todas') return this.activityPerformances

      // Función para normalizar nombres de áreas
      const normalizeAreaName = (name) => {
        if (!name) return ''
        return name.toUpperCase()
          .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
          .replace(/-/g, ' ')
          .replace(/\s+/g, ' ')
          .trim()
      }

      // Buscar academic_area_id correspondiente al nombre de área con normalización
      const normalizedSelected = normalizeAreaName(this.selectedAreaName)
      const matching = this.availableAreas.find(a => normalizeAreaName(a.name) === normalizedSelected)
      if (!matching) return []
      const areaId = matching.id
      return this.activityPerformances.filter(a => a.academic_area_id === areaId)
    }
  },
  
  mounted() {
    this.loadPerformanceDetail()
    this.loadAreaWeights()
    this.loadAvailableAreas()
    this.loadAcademicInfo()
  },

  beforeDestroy() {
    // Limpiar intervalo de cooldown cuando el componente se destruye
    if (this.resendCooldownInterval) {
      clearInterval(this.resendCooldownInterval)
      this.resendCooldownInterval = null
    }
  },
  
  methods: {
    async loadPerformanceDetail() {
      this.loading = true
      try {
        const response = await fetch(`/performance_precalifications/${this.performanceId}.json`)
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }
        
        const data = await response.json()
        
        this.performance = data.performance
        this.activityPerformances = data.activity_performances
        this.signatures = data.signatures
        this.currentUserRole = data.current_user_role
        this.currentUserSigned = data.current_user_signed
        this.canEditScoresFromBackend = data.can_edit_scores !== false // Default true si no viene
        this.committeeMemberNumber = data.committee_member_number || null
        this.unitNameFromBackend = data.academic_unit_name || null
        
        // Cargar información académica después de obtener el performance
        await this.loadAcademicInfo()
      } catch (error) {
        console.error('Error cargando detalle:', error)
        this.$buefy.toast.open({
          message: 'Error al cargar el detalle del desempeño',
          type: 'is-danger',
          duration: 4000
        })
      } finally {
        this.loading = false
      }
    },
    
    getTotalHours(activity) {
      const h1 = parseFloat(activity.hours_completed_semester_1) || 0
      const h2 = parseFloat(activity.hours_completed_semester_2) || 0
      return h1 + h2
    },
    
    activityRequiresEvidence(activity) {
      // Si tiene sai_hours_source=true, las horas vienen de SAI y NO requiere evidencias
      if (activity.sai_hours_source) {
        return false;
      }

      // Verificar si las horas realizadas exceden las comprometidas
      const completedSem1 = parseFloat(activity.hours_completed_semester_1) || 0
      const committedSem1 = parseFloat(activity.hours_committed_semester_1) || 0
      const completedSem2 = parseFloat(activity.hours_completed_semester_2) || 0
      const committedSem2 = parseFloat(activity.hours_committed_semester_2) || 0
      
      return completedSem1 > committedSem1 || completedSem2 > committedSem2
    },
    
    getActivityBoxClass(activity) {
      if (activity.precalification_score !== null) return 'has-background-success-light'
      // Solo marcar como peligro si requiere evidencias Y no las tiene
      if (this.activityRequiresEvidence(activity) && (!activity.evidences || activity.evidences.length === 0)) {
        return 'has-background-danger-light'
      }
      return ''
    },
    
    getStatusTagType(activity) {
      if (activity.precalification_score !== null) return 'is-success'
      // Solo marcar como peligro si requiere evidencias Y no las tiene
      if (this.activityRequiresEvidence(activity) && (!activity.evidences || activity.evidences.length === 0)) {
        return 'is-danger'
      }
      return 'is-warning'
    },
    
    getStatusText(activity) {
      if (activity.precalification_score !== null) return 'Calificada'
      // Solo mostrar "Sin evidencias" si las requiere Y no las tiene
      if (this.activityRequiresEvidence(activity) && (!activity.evidences || activity.evidences.length === 0)) {
        return 'Sin evidencias requeridas'
      }
      return 'Pendiente'
    },
    
    getEvidenceUrl(evidence) {
      // Usar ActiveStorage signed_id para descargar evidencias
      if (evidence.signed_id) {
        return `/rails/active_storage/blobs/${evidence.signed_id}/${evidence.file_name}`
      }
      // Fallback: ruta alternativa si no hay signed_id
      return `/evidences/${evidence.id}`
    },
    
    formatDate(dateString) {
      if (!dateString) return 'N/A'
      const date = new Date(dateString)
      return date.toLocaleDateString('es-CL')
    },
    
    formatSignatureDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `Fecha: ${day}/${month}/${year} - Hora: ${hours}:${minutes}`;
    },
    
    openScoreModal(activity) {
      this.selectedActivity = activity
      this.showScoreModal = true
    },
    
    async onActivityScored(scoreData) {
      this.showScoreModal = false
      
      // Si recibimos datos de la respuesta, actualizar directamente sin recargar
      if (scoreData && scoreData.weighted_score_breakdown) {
        // Actualizar el desglose ponderado localmente
        this.weightedScoreBreakdown = scoreData.weighted_score_breakdown
        
        // Recargar solo las actividades para actualizar la que se calificó
        await this.loadPerformanceDetail()
        
        // Mostrar confirmación
        this.$buefy.toast.open({
          message: 'Nota ponderada actualizada en tiempo real',
          type: 'is-info',
          duration: 2000
        })
      } else {
        // Fallback: recargar todo si no hay datos en la respuesta
        await this.loadPerformanceDetail()
        await this.loadAreaWeights()
      }
    },
    
    async signPrecalification() {
      // Paso 1: Solicitar código de verificación
      this.signingPrecalification = true
      try {
        const response = await fetch(
          `/performance_precalifications/${this.performanceId}/send_verification_code`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRF-Token': this.getCsrfToken()
            }
          }
        )
        
        const data = await response.json()
        
        if (!data.success) {
          this.$buefy.toast.open({
            message: data.error || 'Error al enviar código de verificación',
            type: 'is-danger',
            duration: 4000
          })
          this.signingPrecalification = false
          return
        }
        
        // Paso 2: Mostrar modal personalizado para ingresar código
        this.verificationEmail = data.email
        this.verificationCode = ''
        this.verificationError = ''
        this.verificationSuccess = ''
        this.showVerificationModal = true
        this.signingPrecalification = false
        
        // Enfocar el input del código después de abrir el modal
        this.$nextTick(() => {
          if (this.$refs.verificationInput) {
            this.$refs.verificationInput.focus()
          }
        })
        
      } catch (error) {
        this.$buefy.toast.open({
          message: 'Error al enviar código de verificación',
          type: 'is-danger',
          duration: 4000
        })
        this.signingPrecalification = false
      }
    },
    
    async confirmVerification() {
      if (!this.verificationCode || this.verificationCode.length !== 6) {
        this.verificationError = 'Debe ingresar un código de 6 caracteres'
        this.verificationSuccess = ''
        return
      }
      
      this.verifyingCode = true
      this.verificationError = ''
      this.verificationSuccess = ''
      
      try {
        const verifyResponse = await fetch(
          `/performance_precalifications/${this.performanceId}/verify_and_sign_precalification`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRF-Token': this.getCsrfToken()
            },
            body: JSON.stringify({ code: this.verificationCode })
          }
        )
        
        const verifyData = await verifyResponse.json()
        
        if (verifyData.success) {
          this.$buefy.toast.open({
            message: verifyData.message,
            type: 'is-success',
            duration: 4000
          })
          this.showVerificationModal = false
          this.loadPerformanceDetail()
        } else {
          // Mostrar error inline sin cerrar el modal
          this.verificationError = verifyData.error || 'Código inválido o expirado'
          this.verificationCode = '' // Limpiar el código para que pueda reintentar
          
          // Enfocar el input para que pueda reingresar el código
          this.$nextTick(() => {
            if (this.$refs.verificationInput) {
              this.$refs.verificationInput.focus()
            }
          })
        }
      } catch (error) {
        console.error('Error verificando código:', error)
        this.verificationError = 'Error de conexión al verificar el código'
      } finally {
        this.verifyingCode = false
      }
    },
    
    closeVerificationModal() {
      this.showVerificationModal = false
      this.verificationCode = ''
      this.verificationError = ''
      this.verificationSuccess = ''
      this.signingPrecalification = false
      
      // Limpiar cooldown si existe
      if (this.resendCooldownInterval) {
        clearInterval(this.resendCooldownInterval)
        this.resendCooldownInterval = null
        this.resendCooldownSeconds = 0
      }
    },
    
    getCsrfToken() {
      const meta = document.querySelector('meta[name="csrf-token"]')
      return meta ? meta.content : ''
    },

    async loadAreaWeights() {
      try {
        const response = await fetch(`/performance_precalifications/${this.performanceId}/area_weights.json`)
        if (response.ok) {
          const data = await response.json()
          this.areaWeights = data.weights || data.area_weights || []
          
          // Si hay ponderaciones y actividades calificadas, cargar desglose
          if (this.areaWeights.length > 0) {
            this.loadWeightedScoreBreakdown()
          }
        }
      } catch (error) {
        // Error silencioso
      }
    },

    async loadAvailableAreas() {
      try {
        const response = await fetch(`/performance_precalifications/${this.performanceId}/available_areas.json`)
        if (!response.ok) return
        const data = await response.json()
        this.availableAreas = data.areas || []

        // Construir conteo por nombre (normalizar)
        const counts = {}
        this.availableAreas.forEach(a => {
          // store original name as key
          counts[a.name] = a.activities_count
        })
        this.areaCounts = counts

        // Si la pestaña seleccionada no tiene actividades, resetear a 'Todas'
        if (this.selectedAreaName !== 'Todas' && !this.hasActivitiesForTab(this.selectedAreaName)) {
          this.selectedAreaName = 'Todas'
        }
      } catch (err) {
        console.error('Error cargando áreas disponibles:', err)
      }
    },

    hasActivitiesForTab(tabName) {
      return this.getAreaCount(tabName) > 0
    },

    getAreaCount(tabName) {
      if (!this.areaCounts) return 0
      
      // Función para normalizar nombres de áreas
      const normalizeAreaName = (name) => {
        if (!name) return ''
        return name.toUpperCase()
          .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
          .replace(/-/g, ' ')
          .replace(/\s+/g, ' ')
          .trim()
      }
      
      const normalizedTabName = normalizeAreaName(tabName)
      const foundKey = Object.keys(this.areaCounts || {}).find(k => normalizeAreaName(k) === normalizedTabName)
      if (!foundKey) return 0
      const v = this.areaCounts[foundKey]
      return Number.isFinite(v) ? v : parseInt(v) || 0
    },

    selectArea(tabName) {
      if (tabName !== 'Todas' && !this.hasActivitiesForTab(tabName)) return
      this.selectedAreaName = tabName
    },

    async loadWeightedScoreBreakdown() {
      try {
        const response = await fetch(`/performance_precalifications/${this.performanceId}.json`)
        if (response.ok) {
          const data = await response.json()
          if (data.weighted_score_breakdown) {
            this.weightedScoreBreakdown = data.weighted_score_breakdown
          }
        }
      } catch (error) {
        console.error('Error cargando desglose ponderado:', error)
      }
    },

    openAreaWeightsModal() {
      console.log('📝 Abriendo modal de ponderaciones, isEdit:', this.areaWeights.length > 0)
      console.log('📊 areaWeights actual:', this.areaWeights)
      this.showAreaWeightsModal = true
      this.$nextTick(() => {
        console.log('✅ showAreaWeightsModal está ahora en:', this.showAreaWeightsModal)
      })
    },

    onAreaWeightsSaved() {
      this.showAreaWeightsModal = false
      // Recargar tanto las ponderaciones como los detalles del performance
      this.loadAreaWeights()
      this.loadPerformanceDetail()
      this.$buefy.toast.open({
        message: 'Ponderaciones guardadas exitosamente',
        type: 'is-success',
        duration: 3000
      })
    },

    async resendVerificationCode() {
      if (!this.verificationEmail) {
        this.verificationError = 'Email no disponible. Por favor, intente de nuevo.'
        this.verificationSuccess = ''
        return
      }

      // Validar cooldown
      if (this.resendCooldownSeconds > 0) {
        this.verificationError = `Por favor espere ${this.resendCooldownSeconds}s antes de reenviar de nuevo`
        this.verificationSuccess = ''
        return
      }

      this.resendingCode = true
      this.verificationError = ''
      this.verificationSuccess = ''
      
      try {
        const response = await fetch(
          `/performance_precalifications/${this.performanceId}/send_verification_code`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRF-Token': this.getCsrfToken()
            },
            body: JSON.stringify({ 
              email: this.verificationEmail,
              resend: true // Indicar explícitamente que es un reenvío
            })
          }
        )

        const data = await response.json()

        if (data.success) {
          // Activar cooldown de 60 segundos
          this.resendCooldownSeconds = this.resendCooldownMax
          
          // Iniciar contador de cooldown
          if (this.resendCooldownInterval) {
            clearInterval(this.resendCooldownInterval)
          }
          
          this.resendCooldownInterval = setInterval(() => {
            this.resendCooldownSeconds--
            
            if (this.resendCooldownSeconds <= 0) {
              clearInterval(this.resendCooldownInterval)
              this.resendCooldownInterval = null
            }
          }, 1000)
          
          // Mostrar mensaje de éxito inline
          this.verificationSuccess = `✓ Código reenviado exitosamente a ${this.verificationEmail}`
          this.verificationError = ''
          
          // Limpiar el código y enfocar el input
          this.verificationCode = ''
          this.$nextTick(() => {
            if (this.$refs.verificationInput) {
              this.$refs.verificationInput.focus()
            }
          })
        } else {
          this.verificationError = 'Error al reenviar código: ' + (data.error || 'Error desconocido')
          this.verificationSuccess = ''
        }
      } catch (error) {
        this.verificationError = 'Error de conexión al reenviar código'
        this.verificationSuccess = ''
      } finally {
        this.resendingCode = false
      }
    },

    updateResendButtonState(button) {
      // Este método ya no es necesario con el nuevo modal
      // Se mantiene por compatibilidad pero no se usa
      if (this.resendCooldownSeconds > 0) {
        button.disabled = true
        button.textContent = `⏱️ ${this.resendCooldownSeconds}s`
      } else {
        button.disabled = false
        button.textContent = '↻ Reenviar Código'
      }
    },

    async loadAcademicInfo() {
      if (!this.performance || !this.performance.person_id) return;
      
      try {
        const response = await fetch(`/people/${this.performance.person_id}/my-all-academic-data.json`)
        if (response.ok) {
          this.academicInfo = await response.json()
        }
      } catch (error) {
        console.error('Error cargando información académica:', error)
      }
    },

    async previewReportPDF() {
      this.generatingPdf = true;
      try {
        let width = 216; // 8.5 pulgadas en mm
        let height = 356; // 14 pulgadas en mm
        let doc = new jsPDF({
          orientation: "landscape",
          format: [width, height],
        });
        let pageWidth = doc.internal.pageSize.width;

        // HEADER
        doc.setFont("helvetica", "bold");
        doc.setFontSize(11);
        let header = [
          "UNIVERSIDAD DE SANTIAGO DE CHILE",
          "FACULTAD DE INGENIERÍA",
          this.unitName !== 'N/A' ? this.unitName.toUpperCase() : "DEPARTAMENTO",
        ];
        doc.text(header, 10, 15);

        // TITLE
        let blockTitlePosition = 25;
        doc.setFontSize(12);

        let title = "INFORME DE PRECALIFICACIÓN DE DESEMPEÑO";
        let titleWidth = doc.getTextWidth(title);
        let titleX = (pageWidth - titleWidth) / 2;
        doc.text(title, titleX, blockTitlePosition);

        blockTitlePosition += 6;
        title = `AÑO ${this.performance.year}`;
        titleWidth = doc.getTextWidth(title);
        titleX = (pageWidth - titleWidth) / 2;
        doc.text(title, titleX, blockTitlePosition);

        // Configuration
        let tableMargin = 12;
        let tableWidth = pageWidth - tableMargin * 2;
        let marginLeft = (pageWidth - tableWidth) / 2;

        // ACADEMIC INFORMATION
        let blockAcademicInformationPosition = blockTitlePosition + 10;
        doc.setFont("helvetica", "bold");
        doc.setFontSize(10);
        doc.text("I. Antecedentes Personales", 11, blockAcademicInformationPosition);

        // Período y Jornada
        blockAcademicInformationPosition += 5;
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.text(`Período: Año ${this.performance.year}`, marginLeft, blockAcademicInformationPosition);
        doc.text(`Jornada: ${this.academicInfo?.working_day_name || 'No definida'}`, marginLeft + 100, blockAcademicInformationPosition);

        let columns = [
          this.performance.person.last_name,
          this.performance.person.second_surname || '',
          this.performance.person.name,
        ];
        let rows = [["Primer Apellido", "Segundo Apellido", "Nombres"]];

        blockAcademicInformationPosition += 5;
        doc.autoTable({
          head: [columns],
          body: rows,
          startY: blockAcademicInformationPosition,
          theme: "plain",
          margin: { left: marginLeft },
          columnStyles: {
            0: { cellWidth: tableWidth * 0.33 },
            1: { cellWidth: tableWidth * 0.33 },
            2: { cellWidth: tableWidth * 0.33 },
          },
          styles: {
            fontSize: 8,
            textColor: [0, 0, 0],
            fillColor: [255, 255, 255],
            halign: "center",
          },
          headStyles: {
            fillColor: [255, 255, 255],
            textColor: [0, 0, 0],
            fontSize: 8,
            fontStyle: "bold",
            halign: "center",
          },
        });

        // DETAILED ACTIVITIES - UNA SOLA TABLA (en la misma página)
        let currentY = doc.lastAutoTable.finalY + 15;
        
        // Si no hay espacio suficiente, hacer salto de página
        if (currentY > 280) {
            doc.addPage();
            currentY = 15;
        }
        
        doc.setFont("helvetica", "bold");
        doc.setFontSize(10);
        doc.text("II. Detalle de Actividades y Calificaciones", 11, currentY);
        
        currentY += 5;

        // Agrupar actividades por área
        let activitiesByArea = {};
        this.activityPerformances.forEach(act => {
            if (!activitiesByArea[act.academic_area_id]) {
                activitiesByArea[act.academic_area_id] = [];
            }
            activitiesByArea[act.academic_area_id].push(act);
        });

        // Construir todas las filas para una sola tabla
        let allRows = [];
        
        // Función para normalizar nombres de áreas (remover acentos, guiones, mayúsculas)
        const normalizeAreaName = (name) => {
            if (!name) return '';
            return name.toUpperCase()
                .normalize("NFD").replace(/[\u0300-\u036f]/g, "") // Remover acentos
                .replace(/-/g, ' ') // Reemplazar guiones por espacios
                .replace(/\s+/g, ' ') // Normalizar espacios múltiples
                .trim();
        };
        
        // Función para buscar área en availableAreas con normalización
        const findAreaByName = (searchName) => {
            const normalizedSearch = normalizeAreaName(searchName);
            return this.availableAreas.find(a => normalizeAreaName(a.name) === normalizedSearch);
        };
        
        // Usar todas las áreas académicas disponibles (areaTabs define todas las áreas posibles)
        const allAcademicAreas = [
          'Docencia',
          'Investigación y Desarrollo',
          'Asistencia Técnica',
          'Perfeccionamiento',
          'Administración Académica',
          'Extensión-VIME',
          'Educación Continua-VIME'
        ];
        
        for (const areaName of allAcademicAreas) {
            // Buscar el área en availableAreas con normalización
            let area = findAreaByName(areaName);
            let areaActivities = area ? activitiesByArea[area.id] : null;
            
            const normalizedAreaName = normalizeAreaName(areaName);
            
            // Obtener el peso del área desde weightedScoreBreakdown si está disponible
            let weightPercentage = 0;
            let areaBreakdown = null;
            
            if (this.weightedScoreBreakdown && this.weightedScoreBreakdown.breakdown) {
                areaBreakdown = this.weightedScoreBreakdown.breakdown.find(b => 
                    normalizeAreaName(b.area_name) === normalizedAreaName
                );
                if (areaBreakdown) {
                    weightPercentage = parseFloat(areaBreakdown.weight_percentage);
                }
            }
            
            // Si no está en breakdown, buscar en areaWeights
            if (weightPercentage === 0 && this.areaWeights) {
                let areaWeight = this.areaWeights.find(w => 
                    normalizeAreaName(w.area_name) === normalizedAreaName
                );
                weightPercentage = areaWeight ? parseFloat(areaWeight.weight_percentage) : 0;
            }
            
            // Calcular totales del área
            let totalCommittedSem1 = 0;
            let totalCompletedSem1 = 0;
            let totalCommittedSem2 = 0;
            let totalCompletedSem2 = 0;
            let areaAverageScore = 0;
            let activitiesWithScore = 0;
            
            if (areaActivities && areaActivities.length > 0) {
                areaActivities.forEach(act => {
                    totalCommittedSem1 += parseFloat(act.hours_committed_semester_1) || 0;
                    totalCompletedSem1 += parseFloat(act.hours_completed_semester_1) || 0;
                    totalCommittedSem2 += parseFloat(act.hours_committed_semester_2) || 0;
                    totalCompletedSem2 += parseFloat(act.hours_completed_semester_2) || 0;
                    
                    if (act.precalification_score) {
                        areaAverageScore += parseFloat(act.precalification_score);
                        activitiesWithScore++;
                    }
                });
                
                if (activitiesWithScore > 0) {
                    areaAverageScore = areaAverageScore / activitiesWithScore;
                }
            }
            
            // Si tenemos breakdown, usar su area_average
            if (areaBreakdown && parseFloat(areaBreakdown.area_average) > 0) {
                areaAverageScore = parseFloat(areaBreakdown.area_average);
            }
            
            let totalCommittedAnnual = totalCommittedSem1 + totalCommittedSem2;
            let totalCompletedAnnual = totalCompletedSem1 + totalCompletedSem2;
            let partialScore = '';
            
            // Calcular puntaje parcial usando breakdown si está disponible
            if (areaBreakdown && parseFloat(areaBreakdown.weighted_contribution) > 0) {
                partialScore = parseFloat(areaBreakdown.weighted_contribution).toFixed(2);
            } else if (areaAverageScore > 0 && weightPercentage > 0) {
                partialScore = (areaAverageScore * weightPercentage / 100).toFixed(2);
            }
            
            // Agregar fila de encabezado del área con totales
            allRows.push([
                { content: areaName.toUpperCase(), styles: { fontStyle: 'bold', fillColor: [220, 220, 220], halign: 'left' } },
                { content: totalCommittedSem1 > 0 ? totalCommittedSem1.toFixed(1) : '', styles: { fontStyle: 'bold', fillColor: [220, 220, 220], halign: 'center' } },
                { content: totalCompletedSem1 > 0 ? totalCompletedSem1.toFixed(1) : '', styles: { fontStyle: 'bold', fillColor: [220, 220, 220], halign: 'center' } },
                { content: totalCommittedSem2 > 0 ? totalCommittedSem2.toFixed(1) : '', styles: { fontStyle: 'bold', fillColor: [220, 220, 220], halign: 'center' } },
                { content: totalCompletedSem2 > 0 ? totalCompletedSem2.toFixed(1) : '', styles: { fontStyle: 'bold', fillColor: [220, 220, 220], halign: 'center' } },
                { content: totalCommittedAnnual > 0 ? totalCommittedAnnual.toFixed(1) : '', styles: { fontStyle: 'bold', fillColor: [220, 220, 220], halign: 'center' } },
                { content: totalCompletedAnnual > 0 ? totalCompletedAnnual.toFixed(1) : '', styles: { fontStyle: 'bold', fillColor: [220, 220, 220], halign: 'center' } },
                { content: areaAverageScore > 0 ? areaAverageScore.toFixed(2) : '', styles: { fontStyle: 'bold', fillColor: [220, 220, 220], halign: 'center' } },
                { content: weightPercentage > 0 ? weightPercentage.toFixed(1) + '%' : '', styles: { fontStyle: 'bold', fillColor: [220, 220, 220], halign: 'center' } },
                { content: '', styles: { fillColor: [220, 220, 220] } },
                { content: partialScore, styles: { fontStyle: 'bold', fillColor: [220, 220, 220], halign: 'center' } }
            ]);
            
            // Si hay actividades, agregarlas
            if (areaActivities && areaActivities.length > 0) {
                areaActivities.forEach(act => {
                    let hoursCommittedSem1 = parseFloat(act.hours_committed_semester_1) || 0;
                    let hoursCompletedSem1 = parseFloat(act.hours_completed_semester_1) || 0;
                    let hoursCommittedSem2 = parseFloat(act.hours_committed_semester_2) || 0;
                    let hoursCompletedSem2 = parseFloat(act.hours_completed_semester_2) || 0;
                    let hoursCommittedAnnual = hoursCommittedSem1 + hoursCommittedSem2;
                    let hoursCompletedAnnual = hoursCompletedSem1 + hoursCompletedSem2;
                    
                    let score = act.precalification_score ? parseFloat(act.precalification_score) : 0;
                    
                    allRows.push([
                        act.academic_activity ? act.academic_activity.name : 'Actividad',
                        hoursCommittedSem1.toFixed(1),
                        hoursCompletedSem1.toFixed(1),
                        hoursCommittedSem2.toFixed(1),
                        hoursCompletedSem2.toFixed(1),
                        hoursCommittedAnnual.toFixed(1),
                        hoursCompletedAnnual.toFixed(1),
                        act.precalification_score || '',
                        '', // Ponderación vacía para actividades individuales
                        act.precalification_comment || '',
                        '' // Puntaje parcial vacío para actividades individuales
                    ]);
                });
            } else {
                // Si no hay actividades, agregar fila vacía
                allRows.push(['', '', '', '', '', '', '', '', '', '', '']);
            }
        }
        
        // Calcular totales generales para la fila TOTAL
        let grandTotalCommittedSem1 = 0;
        let grandTotalCompletedSem1 = 0;
        let grandTotalCommittedSem2 = 0;
        let grandTotalCompletedSem2 = 0;
        let grandTotalCommittedAnnual = 0;
        let grandTotalCompletedAnnual = 0;
        
        // Sumar todos los totales de las áreas
        for (const areaName of allAcademicAreas) {
            let area = findAreaByName(areaName);
            let areaActivities = area ? activitiesByArea[area.id] : null;
            
            if (areaActivities && areaActivities.length > 0) {
                areaActivities.forEach(act => {
                    grandTotalCommittedSem1 += parseFloat(act.hours_committed_semester_1) || 0;
                    grandTotalCompletedSem1 += parseFloat(act.hours_completed_semester_1) || 0;
                    grandTotalCommittedSem2 += parseFloat(act.hours_committed_semester_2) || 0;
                    grandTotalCompletedSem2 += parseFloat(act.hours_completed_semester_2) || 0;
                });
            }
        }
        
        grandTotalCommittedAnnual = grandTotalCommittedSem1 + grandTotalCommittedSem2;
        grandTotalCompletedAnnual = grandTotalCompletedSem1 + grandTotalCompletedSem2;
        
        // Calcular suma total de ponderaciones (debería dar 1.00)
        let totalWeightPercentage = 0;
        if (this.weightedScoreBreakdown && this.weightedScoreBreakdown.breakdown) {
            this.weightedScoreBreakdown.breakdown.forEach(b => {
                totalWeightPercentage += parseFloat(b.weight_percentage);
            });
        }
        let totalWeightDisplay = totalWeightPercentage > 0 ? (totalWeightPercentage / 100).toFixed(2) : '';
        
        // Agregar fila TOTAL
        let totalScore = this.weightedScoreBreakdown ? parseFloat(this.weightedScoreBreakdown.total_score).toFixed(2) : '';
        allRows.push([
            { content: 'TOTAL', styles: { fontStyle: 'bold', halign: 'left' } },
            { content: grandTotalCommittedSem1.toFixed(1), styles: { fontStyle: 'bold', halign: 'center' } },
            { content: grandTotalCompletedSem1.toFixed(1), styles: { fontStyle: 'bold', halign: 'center' } },
            { content: grandTotalCommittedSem2.toFixed(1), styles: { fontStyle: 'bold', halign: 'center' } },
            { content: grandTotalCompletedSem2.toFixed(1), styles: { fontStyle: 'bold', halign: 'center' } },
            { content: grandTotalCommittedAnnual.toFixed(1), styles: { fontStyle: 'bold', halign: 'center' } },
            { content: grandTotalCompletedAnnual.toFixed(1), styles: { fontStyle: 'bold', halign: 'center' } },
            { content: totalScore, styles: { fontStyle: 'bold', halign: 'center' } }, // Calif.
            { content: totalWeightDisplay, styles: { fontStyle: 'bold', halign: 'center' } }, // Pond.
            '', // Observaciones vacía
            { content: totalScore, styles: { fontStyle: 'bold', halign: 'center' } } // Puntaje Parcial
        ]);
        
        // Agregar fila sin etiqueta con totales/2 y estado
        allRows.push([
            '', // Actividad vacía
            '', '', '', '', // Semestres vacíos
            { content: (grandTotalCommittedAnnual / 2).toFixed(1), styles: { fontStyle: 'bold', halign: 'center' } },
            { content: (grandTotalCompletedAnnual / 2).toFixed(1), styles: { fontStyle: 'bold', halign: 'center' } },
            { content: this.finalScoreCategory ? this.finalScoreCategory.label.toUpperCase() : '', colSpan: 2, styles: { fontStyle: 'bold', halign: 'center' } }, // Calif. + Pond. (estado)
            '', // Observaciones vacía
            '' // Puntaje Parcial vacío
        ]);

        // Crear una sola tabla con todas las actividades
        doc.autoTable({
            head: [[
                "Actividad",
                { content: "1° Semestre", colSpan: 2 },
                { content: "2° Semestre", colSpan: 2 },
                { content: "Anual", colSpan: 2 },
                "Calif.",
                "Pond.",
                "Observaciones e Información adicional",
                "Puntaje Parcial"
            ], [
                "",
                "Comprometidas",
                "Realizadas",
                "Comprometidas",
                "Realizadas",
                "Comprometidas",
                "Realizadas",
                "",
                "",
                "",
                ""
            ]],
            body: allRows,
            startY: currentY,
            theme: "grid",
            margin: { left: marginLeft },
            columnStyles: {
                0: { cellWidth: tableWidth * 0.18 },  // Actividad
                1: { cellWidth: tableWidth * 0.07 },  // 1°S Comprometidas
                2: { cellWidth: tableWidth * 0.07 },  // 1°S Realizadas
                3: { cellWidth: tableWidth * 0.07 },  // 2°S Comprometidas
                4: { cellWidth: tableWidth * 0.07 },  // 2°S Realizadas
                5: { cellWidth: tableWidth * 0.07 },  // Anual Comprometidas
                6: { cellWidth: tableWidth * 0.07 },  // Anual Realizadas
                7: { cellWidth: tableWidth * 0.05 },  // Calificación
                8: { cellWidth: tableWidth * 0.05 },  // Ponderación
                9: { cellWidth: tableWidth * 0.20 },  // Observaciones
                10: { cellWidth: tableWidth * 0.10 }, // Puntaje Parcial
            },
            styles: { 
                fontSize: 6.5, 
                textColor: [0, 0, 0], 
                halign: "center",
                valign: "middle",
                cellPadding: 1.5
            },
            headStyles: { 
                fillColor: [255, 255, 255], 
                textColor: [0, 0, 0], 
                lineColor: 200, 
                lineWidth: 0.25,
                fontSize: 6.5,
                fontStyle: "bold",
                halign: "center"
            },
            bodyStyles: {
                0: { halign: "left" },   // Actividad alineada a izquierda
                9: { halign: "left" },   // Observaciones alineada a izquierda
            },
            didParseCell: function(data) {
                // Ajustar altura de fila para las observaciones si son largas
                if (data.column.index === 9 && data.cell.raw && data.cell.raw.length > 50) {
                    data.cell.styles.cellPadding = 2;
                }
            }
        });

        // Verificar si están las 3 firmas completas
        const allSignaturesComplete = this.signatures.director.signed && 
                                       this.signatures.par_interno.signed && 
                                       this.signatures.par_externo.signed;

        // Si están las 3 firmas, mostrar sección de firmas
        if (allSignaturesComplete) {
            // SIGNATURES - En nueva página
            doc.addPage();
            let blockSignaturesPosition = 30;
            
            doc.setFont("helvetica", "bold");
            doc.setFontSize(10);
            doc.text("III. Firmas de la Comisión Precalificadora", 11, blockSignaturesPosition);
            blockSignaturesPosition += 40;
            
            // Layout for 3 signatures
            let sigWidth = tableWidth / 3;
            
            // Director
            let x1 = marginLeft + sigWidth * 0.5;
            doc.line(x1 - 30, blockSignaturesPosition, x1 + 30, blockSignaturesPosition);
            doc.setFontSize(8);
            doc.text("Director(a) de Departamento", x1, blockSignaturesPosition + 5, { align: "center" });
            doc.setFont("helvetica", "normal");
            doc.setFontSize(7);
            doc.text("FIRMADO DIGITALMENTE", x1, blockSignaturesPosition - 15, { align: "center" });
            doc.text(this.signatures.director.name, x1, blockSignaturesPosition - 10, { align: "center" });
            if (this.performance.director_signature_date) {
                doc.text(this.formatSignatureDate(this.performance.director_signature_date), x1, blockSignaturesPosition - 5, { align: "center" });
            }

            // Par Interno
            let x2 = marginLeft + sigWidth * 1.5;
            doc.setFont("helvetica", "bold");
            doc.line(x2 - 30, blockSignaturesPosition, x2 + 30, blockSignaturesPosition);
            doc.setFontSize(8);
            doc.text("Comisión Miembro 1", x2, blockSignaturesPosition + 5, { align: "center" });
            doc.setFont("helvetica", "normal");
            doc.setFontSize(7);
            doc.text("FIRMADO DIGITALMENTE", x2, blockSignaturesPosition - 15, { align: "center" });
            doc.text(this.signatures.par_interno.name, x2, blockSignaturesPosition - 10, { align: "center" });
            if (this.performance.committee_member_1_sign_date) {
                doc.text(this.formatSignatureDate(this.performance.committee_member_1_sign_date), x2, blockSignaturesPosition - 5, { align: "center" });
            }

            // Par Externo
            let x3 = marginLeft + sigWidth * 2.5;
            doc.setFont("helvetica", "bold");
            doc.line(x3 - 30, blockSignaturesPosition, x3 + 30, blockSignaturesPosition);
            doc.setFontSize(8);
            doc.text("Comisión Miembro 2", x3, blockSignaturesPosition + 5, { align: "center" });
            doc.setFont("helvetica", "normal");
            doc.setFontSize(7);
            doc.text("FIRMADO DIGITALMENTE", x3, blockSignaturesPosition - 15, { align: "center" });
            doc.text(this.signatures.par_externo.name, x3, blockSignaturesPosition - 10, { align: "center" });
            if (this.performance.committee_member_2_sign_date) {
                doc.text(this.formatSignatureDate(this.performance.committee_member_2_sign_date), x3, blockSignaturesPosition - 5, { align: "center" });
            }
        }

        // LOGO
        let imgPath = "/logo_negro_facultad_ing.png";
        let imgWidth = 60;
        let imgX = pageWidth - imgWidth - 10;
        let imgY = 5;

        try {
            const response = await fetch(imgPath);
            const blob = await response.blob();
            const base64Image = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result);
                reader.onerror = reject;
                reader.readAsDataURL(blob);
            });

            doc.setPage(1);
            doc.addImage(base64Image, "PNG", imgX, imgY, imgWidth, 0);
        } catch (e) {
            console.error("Error loading logo", e);
        }

        // Si NO están las 3 firmas, agregar marca de agua "BORRADOR" en todas las páginas
        if (!allSignaturesComplete) {
            const totalPages = doc.internal.getNumberOfPages();
            const pageHeight = doc.internal.pageSize.height;
            
            for (let i = 1; i <= totalPages; i++) {
                doc.setPage(i);
                
                // Guardar estado gráfico
                doc.saveGraphicsState();
                
                // Aplicar transparencia usando GState
                doc.setGState(new doc.GState({ opacity: 0.12 }));
                
                doc.setFont("helvetica", "bold");
                doc.setFontSize(45);
                doc.setTextColor(180, 180, 180);
                
                // Crear patrón de "BORRADOR" en toda la página
                const rows = 5;
                const cols = 2;
                const stepY = pageHeight / rows;
                const stepX = pageWidth / cols;
                
                for (let row = 0; row < rows; row++) {
                    for (let col = 0; col < cols; col++) {
                        const x = (col + 0.5) * stepX;
                        const y = (row + 0.5) * stepY;
                        
                        doc.text("BORRADOR", x, y, {
                            align: "center",
                            angle: 45
                        });
                    }
                }
                
                // Restaurar estado gráfico (incluyendo opacidad)
                doc.restoreGraphicsState();
                
                // Restaurar color de texto para contenido normal
                doc.setTextColor(0, 0, 0);
            }
        }

        const filename = `INFORME_PRECALIFICACION_${this.performance.person.rut}_${this.performance.year}${!allSignaturesComplete ? '_BORRADOR' : ''}.pdf`;
        
        // Guardar referencia al PDF para preview/descarga
        this.currentPdfDoc = doc;
        this.currentPdfFilename = filename;
        
        // Generar URL para vista previa
        this.pdfPreviewUrl = doc.output('bloburl');
        this.showPdfPreviewModal = true;

      } catch (error) {
        console.error("Error generating PDF", error);
        this.$buefy.toast.open({
            message: 'Error al generar el PDF',
            type: 'is-danger'
        });
      } finally {
        this.generatingPdf = false;
      }
    },

    downloadCurrentPdf() {
      if (this.currentPdfDoc && this.currentPdfFilename) {
        this.currentPdfDoc.save(this.currentPdfFilename);
        this.$buefy.toast.open({
          message: 'Documento descargado exitosamente',
          type: 'is-success'
        });
      }
    },
    // ...existing code...
  }
}
</script>

<style scoped>
.py-6 {
  padding-top: 3rem;
  padding-bottom: 3rem;
}

.has-background-success-light {
  background-color: #f0fff4;
  border-left: 4px solid #48c774;
}

.has-background-danger-light {
  background-color: #fff5f5;
  border-left: 4px solid #f14668;
}

/* Mejoras en enlaces de evidencias */
a.has-text-info {
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

a.has-text-info:hover {
  color: #0a66c2;
  text-decoration: underline;
  transform: translateX(2px);
}

/* Estilos para la sección centrada de calificación */
.columns.is-centered {
  justify-content: center;
}

/* Estilos para el modal de verificación */
.verification-modal-card {
  width: 450px;
  max-width: 95vw;
}

.verification-modal-card .modal-card-head {
  background-color: #f5f5f5;
  border-bottom: 1px solid #dbdbdb;
}

.verification-modal-card .modal-card-title {
  color: #363636;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.verification-modal-card .modal-card-foot {
  justify-content: flex-end;
  gap: 0.5rem;
  background-color: #f5f5f5;
  border-top: 1px solid #dbdbdb;
}

.verification-footer {
  justify-content: center !important;
  flex-wrap: wrap;
}

::v-deep .verification-input input {
  text-align: center;
  font-size: 1.25rem;
  letter-spacing: 0.3rem;
  font-weight: 600;
  font-family: monospace;
}

::v-deep .verification-input input::placeholder {
  color: #b5b5b5;
  letter-spacing: 0.15rem;
}
</style>
